import { readableErrorMessage } from '../utils/appErrors';

export const ACTION_FAILURE_EVENT = 'portal:action-failed';
export const REQUEST_OPERATION_HEADER = 'x-portal-operation';
export const REQUEST_VERSION_CONFLICT_HEADER = 'x-portal-version-conflict-owner';
export type RequestOperation = 'read' | 'mutation' | 'unknown';
const ownedReadCancellations = new WeakSet<object>();

/** This header is consumed locally by guarded fetch, never sent to the service. */
export function markRequest<T extends { setHeader(name: string, value: string): unknown }>(
  request: T,
  operation: Exclude<RequestOperation, 'unknown'>,
  options?: { handlesVersionConflict?: boolean },
): T {
  request.setHeader(REQUEST_OPERATION_HEADER, operation);
  if (options?.handlesVersionConflict) request.setHeader(REQUEST_VERSION_CONFLICT_HEADER, 'caller');
  return request;
}

export function requestOperation(input: RequestInfo | URL, init?: RequestInit): RequestOperation {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  const explicit = headers.get(REQUEST_OPERATION_HEADER);
  if (explicit === 'read' || explicit === 'mutation') return explicit;
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  if (method === 'GET' || method === 'HEAD') return 'read';
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  // An RPC's verb says nothing about its side effects. Its owning caller must
  // classify it; unclassified failures remain with that caller.
  if (/\/rest\/v1\/rpc\//.test(url)) return 'unknown';
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) ? 'mutation' : 'unknown';
}

export function markOwnedReadCancellation(error: unknown): void {
  if (error && typeof error === 'object') ownedReadCancellations.add(error);
}

export function isOwnedReadCancellation(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (ownedReadCancellations.has(error)
    || ('code' in error && error.code === 'PORTAL_READ_CANCELLED')));
}

/** A transport-level fallback for callers that catch a failed save without UI feedback. */
export function reportActionFailure(error: unknown): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ACTION_FAILURE_EVENT, {
    detail: { message: readableErrorMessage(error, undefined, 'mutation') },
  }));
}

export function isWriteRequest(input: RequestInfo | URL, init?: RequestInit): boolean {
  return requestOperation(input, init) === 'mutation';
}

/** Inspect a clone so the original response remains available to its caller. */
export async function reportRejectedAction(response: Response, options?: { handledVersionConflict?: boolean }): Promise<void> {
  const body = await response.clone().json().catch(() => null);
  // A row editor owns the draft and its conflict state. Zero matching versions
  // are not an access denial; leave that expected refusal with the caller.
  if (options?.handledVersionConflict && response.status === 406 && body?.code === 'PGRST116'
    && /0 rows/i.test(String(body?.details))) return;
  reportActionFailure({
    status: response.status,
    code: body?.code,
    message: body?.message ?? body?.error,
    details: body?.details,
  });
}
